import { service } from '@ember/service';
import { cached } from '@glimmer/tracking';

import moment from 'moment';

import type { APIData, DSSetup, RecordData, RequestError, Service } from 'webapp-types';
import {
	ForecastMethod,
	ForecastStatusType,
	GranulationTypeId,
	MetricDetailsTab,
	ModelName,
	TimeframeType,
	TimeUnit,
} from 'webapp/enums/enums';
import { AppEvent } from 'webapp/enums/events';
import { TrackingEvent } from 'webapp/enums/tracking';
import { serializeMetricSetupData } from 'webapp/utils/common';

import DateRange from 'webapp/models-custom/date-range';
import Forecast from 'webapp/models-custom/forecast';
import MetricSetup from 'webapp/models-custom/metric-setup';
import type ForecastRecord from 'webapp/models/forecast';

import { asyncReplyEventName } from 'webapp/services/event-manager';
import ModelManagerForStoreService from 'webapp/services/model-manager/for-store';
import type { RSPromise } from 'webapp/services/request';

type RequestVariablePrefix = 'forecasts' | 'forecastsData';

export const ForecastPusherEvents = {
	FORECAST_PREVIEW_DATA_COMPLETE: 'ForecastPreviewData', // POST /preview
	FORECAST_UPDATE_DATA_COMPLETE: 'ForecastUpdateData', // PUT /snapshot
	METRIC_INSIGHTS_FORECAST_DATA_COMPLETE: 'MetricInsightsForecast', // POST /metric-insights/forecast/data
} as const;

interface TrackingData extends APIData.Tracking.Data {
	metrics: string;
	datasource: string;
	forecasting_period?: string;
	granulation?: string;
	seasonality_type?: string[];
	holidays_country?: string;
}

export default class ModelManagerForecastsService extends ModelManagerForStoreService<
	ForecastRecord,
	Forecast
> {
	@service('navigation') declare navigationService: Service.Navigation;
	@service('constants') declare constantsService: Service.Constants;
	@service('socket') declare socketService: Service.Socket;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('model-manager/metrics') declare mmMetricsService: Service.ModelManager.Metrics;
	@service('account') declare accountService: Service.Account;
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('popup') declare popupService: Service.Popup;
	@service('event-manager') declare eventManagerService: Service.EventManager;
	@service('metric-details')
	declare metricDetailsService: Service.MetricDetails;

	private _forecastModels: Forecast[] = [];

	@cached
	get forecastModels(): Forecast[] {
		this.updateModelCache(this._forecastModels, this.savedRecords);
		return this._forecastModels;
	}

	get firstDayOfTheWeekErrorText() {
		const tooltipButton = this.accountService.hideDataboxNotWhiteLabeledContentForNonAdmins
			? ''
			: '<a role="button" class="" href="https://help.databox.com/how-to-set-the-first-day-of-the-week-and-time-zones">Learn more</a>';

		return `<div>
			<p><b>Change account settings</b></p>
			<p>
				To view the weekly forecast, the account's first day of the week must be set to Monday.<br>
				${tooltipButton}
			</p>
		</div>`;
	}

	getValidationErrorText(errorData: RequestError) {
		if (errorData.code !== 422) {
			return;
		}

		const errorMessage = errorData.errors[0]?.message;
		return `<div>
			<p>${errorMessage}</p>
		</div>`;
	}

	isStatisticalMethod(method: ForecastMethod): boolean {
		return [
			ForecastMethod.SIMPLE_EXPONENTIAL,
			ForecastMethod.LINEAR_CHANGE,
			ForecastMethod.MOVING_AVERAGE_6M,
		].includes(method);
	}

	getMethodologyDisplayText(method: ForecastMethod): string {
		switch (method) {
			case ForecastMethod.DATABOX:
				return 'Databox forecasting methodology';
			case ForecastMethod.LINEAR_REGRESSION:
				return 'linear regression';
			case ForecastMethod.SIMPLE_EXPONENTIAL:
				return 'simple exponential smoothing';
			case ForecastMethod.LINEAR_CHANGE:
				return 'linear change';
			case ForecastMethod.MOVING_AVERAGE_6M:
				return 'six month moving average';
			//case ForecastMethod.MANUAL:
			//	return 'manual input';
			default:
				return 'Databox forecasting methodology';
		}
	}

	timeUnitToGranulationTypeId(timeUnit: TimeUnit): GranulationTypeId {
		const mapping: Record<TimeUnit, GranulationTypeId> = {
			[TimeUnit.TOTAL]: GranulationTypeId.TOTAL,
			[TimeUnit.HOUR]: GranulationTypeId.HOURLY,
			[TimeUnit.DAY]: GranulationTypeId.DAILY,
			[TimeUnit.WEEK]: GranulationTypeId.WEEKLY,
			[TimeUnit.MONTH]: GranulationTypeId.MONTHLY,
			[TimeUnit.QUARTER]: GranulationTypeId.QUARTERLY,
			[TimeUnit.YEAR]: GranulationTypeId.YEARLY,
		};
		return mapping[timeUnit];
	}

	granulationTypeIdToTimeUnit(granulationTypeId: GranulationTypeId): TimeUnit {
		const mapping: Record<GranulationTypeId, TimeUnit> = {
			[GranulationTypeId.TOTAL]: TimeUnit.TOTAL,
			[GranulationTypeId.HOURLY]: TimeUnit.HOUR,
			[GranulationTypeId.DAILY]: TimeUnit.DAY,
			[GranulationTypeId.WEEKLY]: TimeUnit.WEEK,
			[GranulationTypeId.MONTHLY]: TimeUnit.MONTH,
			[GranulationTypeId.QUARTERLY]: TimeUnit.QUARTER,
			[GranulationTypeId.YEARLY]: TimeUnit.YEAR,
		};
		return mapping[granulationTypeId];
	}

	requestForecastPreview(
		data: APIData.Forecast.PreviewRequest,
	): RSPromise<APIData.Forecast.PreviewResponse> {
		return this.requestService.api('POST', '/forecasts/preview', data, { timeout: 120 * 1000 });
	}

	requestInsightsForecastData(
		dateRange: DateRange,
		metricSetup: MetricSetup,
		onResponse: (forecastData: APIData.MetricInsights.ForecastDataResponse | null) => boolean, // returns true to unsubscribe
		onError: (errorData: RequestError) => void,
	): string {
		const data = {
			metricSetup: serializeMetricSetupData(metricSetup.metricSetupData),
			dateRange: dateRange.formula,
		};

		const requestId = this.requestService.generateRequestId();

		this.requestService
			.apiWithAsyncReply(
				'POST',
				'/metric-insights/forecast/data',
				data,
				{ requestId, timeout: 60 * 1000, skipShowingErrorInfolayer: true },
				(eventData) => onResponse(eventData.metricInsightsForecast!), // the forecast data reply always carries it
			)
			.catch((errorData: RequestError) => {
				onError(errorData);
			});

		return requestId;
	}

	unsubscribeFromInsightsForecastRequest(requestId: string): void {
		const eventName = asyncReplyEventName(
			ForecastPusherEvents.METRIC_INSIGHTS_FORECAST_DATA_COMPLETE,
			requestId,
		);
		this.eventManagerService.off(eventName, this.requestService);
	}

	requestMetricCorrelation(
		firstMetricSetup: DSSetup.MetricSetupData,
		secondMetricSetup: DSSetup.MetricSetupData,
	): RSPromise<APIData.MetricCorrelation.Response> {
		const payload = {
			firstMetricSetup: serializeMetricSetupData(firstMetricSetup),
			secondMetricSetup: serializeMetricSetupData(secondMetricSetup),
		};

		return this.requestService.api('POST', '/metric-correlations/local/calculate', payload, {
			timeout: 20 * 1000,
			skipShowingErrorInfolayer: true,
		});
	}

	requestForecastHolidayOptions() {
		const alreadyRequested = this.constantsService.forecastHolidaysCountries.length;
		if (alreadyRequested) {
			return Promise.resolve();
		} else {
			return this.requestService
				.api<APIData.Forecast.HolidaysResponse>('GET', '/forecasts/holidays')
				.then((reply) => {
					// prettier-ignore

					// reply = {"countries": [{"value":"SI","label":"Slovenia"},{"value":"US","label":"US of A"},{"value":"LV","label":"Latvia"}],"financialMarkets":[{"value":"ECB","label":"EU Central Bank"}, {"value":"smth","label":"Whatever"}]};
					this.constantsService.forecastHolidaysCountries = reply?.countries || [];
				})
				.catch(this.requestService.showAllValidationErrors.bind(this.requestService));
		}
	}

	getIsTheFirstDayOfTheWeekError(errorData: RequestError) {
		if (errorData.obj?.statusText !== 'abort') {
			const errorMessage = errorData.errors[0]?.message;
			return !!errorMessage?.includes('first day of the week');
		}

		return false;
	}

	getTrackingData(
		metricSetup: MetricSetup | null,
		forecastDateRange?: DateRange,
		seasonality: TimeUnit[] = [],
		holidaysCountry?: string | null,
	) {
		const metric = metricSetup?.metric;
		if (metric) {
			const trackingData: TrackingData = {
				metrics: metric.name,
				datasource: metric.dataSourceTypeKey,
			};

			if (forecastDateRange) {
				trackingData.forecasting_period = forecastDateRange.rangeTitleUX;
				trackingData.granulation = forecastDateRange.granulationTitleUX;
			}

			if (seasonality.length) {
				trackingData.seasonality_type = seasonality.map((t) =>
					DateRange.getTimeUnitText(t),
				);
			}

			if (holidaysCountry) {
				trackingData.holidays_country = holidaysCountry;
			}

			return trackingData;
		}
		return null;
	}

	trackEvent(event: TrackingEvent, data: TrackingData) {
		this.analyticsService.event(event, data);
	}

	getForecastingPeriodForTracking(forecast: Forecast): string {
		const endDate = forecast.endDate;
		const timeframe = forecast.timeframe;

		if (endDate) {
			// "end of Month YYYY"
			return `end of ${forecast.getTimeframeDisplayString()}`;
		} else if (timeframe) {
			// use the forecast's existing display string (e.g., "2024", "Q1 2024")
			return forecast.getTimeframeDisplayString();
		}

		return `${new Date().getFullYear()}`;
	}

	trackForecastMetricEvent(event: TrackingEvent, forecast: Forecast, origin: string): void {
		const metric = forecast.metricSetup?.metric;
		if (!metric) {
			return;
		}

		this.analyticsService.event(event, {
			metrics: metric.name,
			datasource: metric.dataSourceTypeKey,
			forecasting_period: this.getForecastingPeriodForTracking(forecast),
			origin,
		});
	}

	duplicateForecast(forecast: Forecast): Forecast {
		// create a temporary record (not persisted to store) with data from existing forecast
		const copyData = forecast.toJSON();
		const newRecord = this.createNewRecord(copyData);
		return this.createModelFromRecord(newRecord);
	}

	// ------ Forecast actions (shared by grid and related-forecasts) ------

	openForecastDetails(model: Forecast): void {
		this.metricDetailsService.openMetricDetailsDrawer({
			metricSetup: model.metricSetup,
			tab: MetricDetailsTab.FORECAST,
			forecast: { forecastId: model.id },
		});
		model.trackEvent(TrackingEvent.FORECAST_DETAILS_VIEWED);
	}

	recalculateForecast(model: Forecast): void {
		model.updateTimeframeToCurrent();

		const layer = this.infolayerService.loading('Recalculating forecast...');

		this.requestService
			.api('PUT', `/forecasts/${model.id}/snapshot`, {
				forecast: {
					timeframe: model.timeframe,
					endDate: model.endDate,
					granulation: model.granulation,
					metricSetup: serializeMetricSetupData(model.metricSetup.metricSetupData)!,
					seasonality: model.seasonality,
					holidaysCountry: model.holidaysCountry,
					methodology: model.methodology,
					regressors: model.buildRegressorsPayload(),
					comparisonPeriod: model.comparisonPeriod,
				},
			})
			.then(
				() => {
					layer.ok('Forecast recalculated!');
				},
				(errorData) => {
					layer.close();
					throw errorData;
				},
			)
			.catch(this.requestService.showAllValidationErrors.bind(this.requestService));
	}

	deleteForecast(model: Forecast): void {
		this.popupService.openConfirmPopup({
			onConfirm: () => {
				const layer = this.infolayerService.loading('Deleting saved forecast...');

				model.trackEvent(TrackingEvent.FORECAST_REMOVED);
				model
					.delete()
					.then(
						() => {
							layer.ok('Saved forecast deleted!');
						},
						(errorData) => {
							layer.close();
							throw errorData;
						},
					)
					.catch(this.requestService.showAllValidationErrors.bind(this.requestService));
			},
			title: 'Are you sure you want to delete this forecast?',
			confirmAction: 'Delete',
			cancelAction: 'Cancel',
			text: '',
		});
	}

	// ------ Async flow / Data requests ------

	// track which requests need notifications when user navigates away
	// socket.ts checks this to show notification when user is not on explorer
	private pendingForecastNotifications = new Set<string>();

	// store completed forecast data for reconstruction after navigation
	// when user clicks notification, reconstruct the forecast from this data
	private pendingForecastDataByRequestId = new Map<
		string,
		APIData.Forecast.NormalizedForecastEventData
	>();

	registerForNotification(requestId: string): void {
		this.pendingForecastNotifications.add(requestId);
	}

	unregisterNotification(requestId: string): void {
		this.pendingForecastNotifications.delete(requestId);
	}

	shouldShowNotification(requestId: string): boolean {
		return this.pendingForecastNotifications.has(requestId);
	}

	storeForecastData(
		requestId: string,
		eventData: APIData.Forecast.NormalizedForecastEventData,
	): void {
		this.pendingForecastDataByRequestId.set(requestId, eventData);
	}

	getPendingForecast(requestId: string): APIData.Forecast.NormalizedForecastEventData | null {
		const data = this.pendingForecastDataByRequestId.get(requestId);
		if (data) {
			this.pendingForecastDataByRequestId.delete(requestId);
			return data;
		}
		return null;
	}

	handleForecastNotificationDisplay(
		eventData: APIData.Forecast.NormalizedForecastEventData,
	): void {
		const requestId = eventData.requestId;

		// check if notification should be shown (component registered but didn't handle it)
		if (!this.shouldShowNotification(requestId)) {
			return; // component handled it directly or never registered
		}

		const forecastConfig = eventData.forecast.config;
		const statusObject = forecastConfig.statusObject;

		// determine if there's an error using statusObject (not success flag)
		const hasError =
			statusObject &&
			(statusObject.type === ForecastStatusType.PREVIEW_GENERATION_FAILED ||
				statusObject.type === ForecastStatusType.SNAPSHOT_GENERATION_FAILED);

		if (hasError) {
			const errorMessage =
				statusObject.data?.message ||
				statusObject.data?.type ||
				'An error occurred while calculating the forecast. Please attempt your action again. In case this error is still persisting, report it to our technical support.';

			this.infolayerService.error(errorMessage);
		} else {
			const forecastTitle = forecastConfig.title || 'Your forecast';

			this.infolayerService.okWithActionButton(`${forecastTitle} is ready!`, {
				text: 'See forecast',
				onClick: () => {
					const handled = this.eventManagerService.trigger(
						AppEvent.FORECAST_DRAWER_SWITCH_TO_FORECAST_TAB,
						{ requestId },
					);

					if (!handled) {
						// build MetricSetup from stored event data so the drawer has full context
						// use .get() (not getPendingForecast) to avoid consuming the data —
						// the forecast explorer may still need it when it mounts.
						const eventData = this.pendingForecastDataByRequestId.get(requestId);
						const metricSetupData = eventData?.forecast?.config?.metricSetup;
						const metricSetup = metricSetupData
							? new MetricSetup(metricSetupData)
							: undefined;

						this.metricDetailsService.openMetricDetailsDrawer(
							{
								metricSetup,
								tab: MetricDetailsTab.FORECAST,
								forecast: { requestId },
							},
							'forecasts',
						);
					}
				},
			});
		}

		this.unregisterNotification(requestId);
	}

	_forecastsRequested = false;
	_forecastsRequestPromise: Promise<void> | null = null;
	requestForecasts(): Promise<void> {
		const variablePrefix = 'forecasts';
		return this.requestService.dataRequestOnce<typeof variablePrefix>(
			this,
			variablePrefix,
			() => {
				// const testData = window.dbx_mock['GET /d/forecasts'];
				// return Promise.resolve(testData);

				return this.requestService.storeFindAll(ModelName.FORECAST);
			},
		);
	}

	_forecastsDataRequested = false;
	_forecastsDataRequestPromise: Promise<void> | null = null;
	requestForecastsData(preStoreFn?: () => Promise<void>): Promise<void> {
		// data itself will be sent async over pusher after data request
		// so we need to trigger it after loading models themselves to avoid data being garbage-collected
		const preRequestPromise = this.requestForecasts().then(
			() => this.socketService.afterDataChannelSubscribed,
		);
		return preRequestPromise.then(() => {
			void this.requestOnce(
				'forecastsData',
				() => {
					// const testData = window.dbx_mock['GET /d/forecasts/data'];
					// return Promise.resolve(testData);

					return this.requestService.api<RecordData.Payload>('GET', '/forecasts/data');
				},
				preStoreFn,
			);
		});
	}

	requestOnce(
		variablePrefix: RequestVariablePrefix,
		requestPromiseFn: () => Promise<object | void>,
		preStoreFn?: () => Promise<void>,
	): Promise<void> {
		return this.requestService.dataRequestOnce<RequestVariablePrefix>(
			this,
			variablePrefix,
			requestPromiseFn,
			preStoreFn,
		);
	}

	// normalizes forecast pusher events (preview and update) into a consistent structure
	normalizeForecastEventData(
		data: APIData.App.BgEvent,
		origin: string,
	): APIData.Forecast.NormalizedForecastEventData {
		// the socket service sets requestId and success on the event before normalizing it
		const requestId = data.requestId!,
			success = data.success!;

		if (origin === ForecastPusherEvents.FORECAST_PREVIEW_DATA_COMPLETE) {
			// preview event: data.forecast contains all preview data
			const previewData = data.forecast;

			// failed preview without forecast data - normalize to a failed status so listeners show an error
			if (!previewData?.forecast) {
				return {
					origin: ForecastPusherEvents.FORECAST_PREVIEW_DATA_COMPLETE,
					requestId,
					success: false,
					forecast: {
						config: {
							statusObject: {
								timestamp: new Date().getTime(),
								type: ForecastStatusType.PREVIEW_GENERATION_FAILED,
								data: {
									message: data.message || 'Forecast preview generation failed',
								},
							},
						} as APIData.Forecast.ForecastConfigPayload,
					},
				};
			}

			const forecastConfig = previewData.forecast;

			return {
				origin: ForecastPusherEvents.FORECAST_PREVIEW_DATA_COMPLETE,
				requestId,
				success,
				forecast: {
					config: forecastConfig,
				},
				previewData: {
					allowedSeasonality: previewData.allowedSeasonality,
					confidenceScore: previewData.confidenceScore,
					dataSelects: previewData.dataSelects,
					monthsOfUsedHistoricalData: previewData.monthsOfUsedHistoricalData,
					regressorPreviewData: previewData.regressorPreviewData,
					sample: previewData.sample,
				},
			};
		} else if (origin === ForecastPusherEvents.FORECAST_UPDATE_DATA_COMPLETE) {
			// raw forecast payload from PUT /snapshot — read BEFORE pushPayload flattens it via the serializer
			const forecastPayload = data.forecasts?.[0] || ({} as RecordData.Forecast.Raw); // always sent with the update event

			return {
				origin: ForecastPusherEvents.FORECAST_UPDATE_DATA_COMPLETE,
				requestId,
				success,
				forecast: {
					id: forecastPayload.id,
					config: {
						comparisonPeriod: forecastPayload.comparisonPeriod,
						endDate: forecastPayload.endDate,
						granulation: forecastPayload.granulation,
						methodology: forecastPayload.methodology,
						metricSetup: forecastPayload.metricSetup as DSSetup.MetricSetupData,
						regressors: forecastPayload.regressors || [],
						seasonality: forecastPayload.seasonality,
						holidaysCountry: forecastPayload.holidaysCountry,
						timeframe: forecastPayload.timeframe,
						title: forecastPayload.title,
						statusObject: forecastPayload.statusObject,
					},
				},
				updateData: {
					lastSnapshotId: forecastPayload.lastSnapshotId,
					samples: data.samples || [],
				},
			};
		}

		throw new Error('Invalid forecast event type');
	}

	getForecastEndTimestamp(forecast: Forecast): number {
		if (forecast?.endDate) {
			return moment(forecast.endDate, 'YYYY_MM').endOf('month').valueOf();
		} else if (forecast?.timeframe) {
			const { type, year } = forecast.timeframe;

			switch (type) {
				case TimeframeType.Y:
					return moment(`${year}-12-31`).endOf('day').valueOf();
				case TimeframeType.Q1:
					return moment(`${year}-03-31`).endOf('day').valueOf();
				case TimeframeType.Q2:
					return moment(`${year}-06-30`).endOf('day').valueOf();
				case TimeframeType.Q3:
					return moment(`${year}-09-30`).endOf('day').valueOf();
				case TimeframeType.Q4:
					return moment(`${year}-12-31`).endOf('day').valueOf();
				case TimeframeType.H1:
					return moment(`${year}-06-30`).endOf('day').valueOf();
				case TimeframeType.H2:
					return moment(`${year}-12-31`).endOf('day').valueOf();
				default:
					return moment().endOf('year').valueOf();
			}
		}

		return moment().endOf('year').valueOf();
	}

	// ------ For-store ------

	modelName = ModelName.FORECAST;

	createModel(model: ForecastRecord): Forecast {
		return new Forecast({ model });
	}
}

declare module '@ember/service' {
	interface Registry {
		'model-manager/forecasts': ModelManagerForecastsService;
	}
}
